/**
 * Minimal, dependency-free markdown renderer for JOBUWIN legal documents.
 *
 * Supports the subset used by our Terms / Privacy markdown: h1–h3 headings, paragraphs,
 * `*` bullet lists, `---` horizontal rules, `**bold**`, inline links, and hard line breaks
 * (two trailing spaces). Content is our own trusted source, but we still render via React
 * elements (no dangerouslySetInnerHTML).
 */

let keySeed = 0;
function nextKey(prefix) {
  keySeed += 1;
  return `${prefix}-${keySeed}`;
}

/** Parse inline `**bold**` and `[text](url)` into React nodes. */
function renderInline(text) {
  const nodes = [];
  let remaining = String(text);
  const pattern = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/;

  while (remaining.length > 0) {
    const match = remaining.match(pattern);
    if (!match) {
      nodes.push(remaining);
      break;
    }
    if (match.index > 0) {
      nodes.push(remaining.slice(0, match.index));
    }
    if (match[1]) {
      nodes.push(<strong key={nextKey("b")}>{match[2]}</strong>);
    } else if (match[3]) {
      const href = match[5];
      const isExternal = /^https?:\/\//i.test(href);
      nodes.push(
        <a
          key={nextKey("a")}
          href={href}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {match[4]}
        </a>
      );
    }
    remaining = remaining.slice(match.index + match[0].length);
  }

  return nodes;
}

/** Inline text that may contain hard line breaks (two trailing spaces before a newline). */
function renderMultilineInline(text) {
  const lines = String(text).split("\n");
  const out = [];
  lines.forEach((line, idx) => {
    out.push(...renderInline(line.replace(/\s+$/, "")));
    if (idx < lines.length - 1) out.push(<br key={nextKey("br")} />);
  });
  return out;
}

export default function LegalMarkdown({ markdown }) {
  keySeed = 0;
  const source = String(markdown || "").replace(/\r\n/g, "\n");
  const lines = source.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") {
      i += 1;
      continue;
    }

    if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
      blocks.push(<hr key={nextKey("hr")} className="jw-legal-hr" />);
      i += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const Tag = `h${Math.min(level, 4)}`;
      blocks.push(<Tag key={nextKey("h")}>{renderInline(heading[2])}</Tag>);
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul key={nextKey("ul")} className="jw-legal-list">
          {items.map((item) => (
            <li key={nextKey("li")}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      lines[i].trim() !== "---" &&
      !/^(#{1,6})\s+/.test(lines[i].trim()) &&
      !/^\s*[-*]\s+/.test(lines[i])
    ) {
      paragraphLines.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={nextKey("p")}>{renderMultilineInline(paragraphLines.join("\n"))}</p>
    );
  }

  return <div className="jw-legal-prose">{blocks}</div>;
}
